import type { VehicleType, Vehicle, Resource, Reservation } from '../types';
import { getCodeById } from '../lib/statusMapper';

const normalizeString = (value: unknown): string => {
  if (value == null) return '';
  const text = String(value).trim();
  return text;
};

const normalizeId = (value: unknown): string | null => {
  const text = normalizeString(value);
  return text || null;
};

const resolveColor = (...candidates: unknown[]): string => {
  for (let i = 0; i < candidates.length; i += 1) {
    const color = normalizeString(candidates[i]);
    if (color) {
      return color;
    }
  }
  return '';
};

export const mapVehicleType = (entry: any): VehicleType | null => {
  if (!entry || typeof entry !== 'object') return null;
  const id = normalizeId(entry.id ?? entry.vehicle_type_id ?? entry.vehicle_type_code);
  const code = normalizeString(entry.vehicle_type_code);
  const name = normalizeString(entry.vehicle_type_name || code);
  const bgColor = normalizeString(entry.bg_color) || '#2563eb';
  const textColor = normalizeString(entry.text_color) || null;
  const priority = Number.isFinite(Number(entry.vehicle_type_priority))
    ? Number(entry.vehicle_type_priority)
    : null;

  return {
    id,
    code,
    name,
    bg_color: bgColor,
    text_color: textColor,
    color1: bgColor,
    color: bgColor,
    priority,
    vehicle_type_code: code,
    vehicle_type_name: name,
    vehicle_type_category: normalizeString(entry.vehicle_type_category),
    vehicle_type_capacity: entry.vehicle_type_capacity ?? null,
    vehicle_type_priority: priority,
    vehicle_type_active: Boolean(entry.vehicle_type_active !== false),
    created_at: entry.created_at ?? null,
    updated_at: entry.updated_at ?? null,
  };
};

const mapVehicleTypeForVehicle = (vehicleType: any): VehicleType | null => {
  const mapped = mapVehicleType(vehicleType);
  if (!mapped) return null;
  return {
    ...mapped,
    id: mapped.id ?? normalizeId(vehicleType?.id),
  };
};

const normalizeBoolean = (value: unknown): boolean => {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  return false;
};

export const mapVehicle = (entry: any): Vehicle | null => {
  if (!entry || typeof entry !== 'object') return null;
  const id = entry.id ?? entry.vehicle_code ?? entry.resource_id;
  const resourceId = entry.id ?? entry.resource_id ?? entry.vehicle_code;
  const vehicleType = mapVehicleTypeForVehicle(entry.vehicle_type);
  const vehicleTypeId = vehicleType?.id ?? null;
  const vehicleCode = normalizeString(entry.vehicle_code);
  const vehicleNumber = normalizeString(entry.vehicle_number);
  const bgColor = resolveColor(entry.bg_color, vehicleType?.bg_color, vehicleType?.color1, vehicleType?.color);
  const textColor = resolveColor(entry.text_color, vehicleType?.text_color);

  // json_data: 文字列の場合はパース（v1.5.0追加）
  let jsonData = entry.json_data;
  if (typeof jsonData === 'string' && jsonData.trim()) {
    try {
      jsonData = JSON.parse(jsonData);
    } catch (e) {
      console.warn('dispatchMappers', 'failed to parse vehicle json_data', { id, error: e });
    }
  }

  return {
    ...entry,
    id,
    vehicleId: id,
    vehicle_id: id,
    resource_id: resourceId,
    resourceId,
    vehicle_code: vehicleCode,
    vehicle_number: vehicleNumber,
    completion: entry.completion ?? entry.detail_completion ?? null,
    active: normalizeString(entry.active) || (entry.active ?? null),
    active_from: entry.active_from ?? null,
    active_to: entry.active_to ?? null,
    status: entry.status ?? null,
    status_name: normalizeString(entry.status_name),
    status_code: normalizeString(entry.status_code),
    vehicle_type: vehicleType,
    car_type: vehicleType?.vehicle_type_name ?? vehicleType?.vehicle_type_code ?? null,
    car_type_id: vehicleTypeId,
    car_type_code: vehicleType?.vehicle_type_code ?? null,
    vehicle_type_id: vehicleTypeId,
    vehicle_type_page_id: vehicleTypeId,
    vehicle_type_page_ids: vehicleTypeId ? [vehicleTypeId] : [],
    bg_color: bgColor || null,
    text_color: textColor || null,
    check_situations: Array.isArray(entry.check_situations) ? entry.check_situations : [], // v1.3.0追加
    priority: entry.reservation_box_priority ?? entry.priority ?? null, // 車両順番用
    out_of_use: normalizeBoolean(entry.out_of_use), // v1.4.0追加
    json_data: jsonData ?? null, // v1.5.0追加
  };
};

export const mapResource = (entry: any): Resource | null => {
  if (!entry || typeof entry !== 'object') return null;
  const vehicle = entry.vehicle ? mapVehicle(entry.vehicle) : null;
  const resourceId = entry.resource_name ?? entry.id;
  const id = entry.id ?? resourceId;
  const vehicleType = vehicle?.vehicle_type ?? null;
  const bgColor = resolveColor(entry.bg_color, vehicle?.bg_color, vehicleType?.bg_color, vehicleType?.color1, vehicleType?.color);
  const textColor = resolveColor(entry.text_color, vehicle?.text_color, vehicleType?.text_color);

  return {
    ...entry,
    id,
    resource_id: resourceId,
    resourceId,
    // resource_name: APIからのオリジナル値を保持（ヘッダー下段表示用）
    resource_name: entry.resource_name ?? null,
    name: normalizeString(entry.resource_name || resourceId),
    priority: Number.isFinite(Number(entry.priority)) ? Number(entry.priority) : null,
    bg_color: bgColor || '#2563eb',
    text_color: textColor || null,
    vehicleId: vehicle?.id ?? null,
    vehicle_id: vehicle?.id ?? null,
    vehicle,
    car_type: vehicle?.car_type ?? null,
    car_type_id: vehicle?.car_type_id ?? null,
    car_type_code: vehicle?.car_type_code ?? null,
    vehicle_type_page_id: vehicle?.vehicle_type_page_id ?? null,
    vehicle_type_page_ids: vehicle?.vehicle_type_page_ids ?? [],
    out_of_use: Boolean(entry.out_of_use || vehicle?.out_of_use),
  };
};

const mapReservationDetail = (detail: any): Record<string, any> => {
  if (!detail || typeof detail !== 'object') return {};
  return {
    // API準拠キー名
    contact_id: normalizeString(detail.contact_id),
    contact_media: normalizeString(detail.contact_media),
    contact_plan: detail.contact_plan ?? null,
    pax: detail.pax ?? null,
    pax_children: detail.pax_children ?? null,
    child_seat: detail.child_seat ?? null,
    booster_seat: detail.booster_seat ?? null,
    junior_seat: detail.junior_seat ?? null,
    arrival_flight: normalizeString(detail.arrival_flight),
    departure_flight: normalizeString(detail.departure_flight),
    base_price: detail.base_price ?? null,
    option_price: detail.option_price ?? null,
    noc: detail.noc ?? null,
    noc_price: detail.noc_price ?? null,
    cdw: detail.cdw ?? null,
    cdw_price: detail.cdw_price ?? null,
    sum_price: detail.sum_price ?? null,
    days: detail.days ?? null,
    nights: detail.nights ?? null,
    invoice_link: normalizeString(detail.invoice_link),
    comment: detail.comment ?? null,
    memo: detail.memo ?? null,
    option: detail.option ?? null,
    // 確認フラグ
    license: detail.license ?? null,
    payment: detail.payment ?? null,
    terms_and_conditions: detail.terms_and_conditions ?? null,
    disclaimer: detail.disclaimer ?? null,
    agreement: detail.agreement ?? null,
    // 連絡手段
    communication_tools: detail.communication_tools ?? [],
    communication_tools_display: detail.communication_tools_display ?? null,
    // 緊急連絡先
    emergency_name: detail.emergency_name ?? null,
    emergency_rel: detail.emergency_rel ?? null,
    emergency_tel: detail.emergency_tel ?? null,
  };
};

export const mapReservation = (entry: any): Reservation | null => {
  if (!entry || typeof entry !== 'object') return null;
  const vehicle = entry.vehicle ? mapVehicle(entry.vehicle) : null;
  const detail = mapReservationDetail(entry.detail);
  const id = entry.id ?? entry.reservation_id ?? entry.reservationId;
  const start = normalizeString(entry.start_date);
  const end = normalizeString(entry.end_date);
  const rawStatusCode = normalizeString(entry.status_code);
  const rawStatusName = normalizeString(entry.status_name);
  const fallbackStatus = normalizeString(entry.status);
  const statusId = entry.status_id ?? entry.statusId;
  const statusCode = rawStatusCode || fallbackStatus || (statusId ? getCodeById(statusId) : null);

  // Preserve the original vehicle_id from API
  const originalVehicleId = entry.vehicle_id ?? null;

  // resource_id is the primary identifier (resource_id === vehicle_id)
  // Resolve from multiple sources with resource_id taking priority
  const resourceId = normalizeId(
    entry.vehicle_id
    ?? entry.vehicleId
    ?? entry.resource_id
    ?? entry.resourceId
    ?? vehicle?.id
    ?? vehicle?.vehicle_code
  );

  // vehicle_id is an alias for resource_id
  // Use the same value to ensure consistency
  const vehicleId = resourceId;

  const bgColor = normalizeString(entry.bg_color);
  const textColor = normalizeString(entry.text_color);

  let jsonData = entry.json_data;
  if (typeof jsonData === 'string' && jsonData.trim()) {
    try {
      jsonData = JSON.parse(jsonData);
    } catch (e) {
      console.warn('dispatchMappers', 'failed to parse json_data', { id, error: e });
    }
  }

  return {
    ...entry,
    json_data: jsonData,
    id,
    reservation_id: id,
    resource_id: resourceId,
    resourceId,
    vehicleId: vehicleId,
    vehicle_id: originalVehicleId ?? vehicleId, // Preserve original API value
    vehicle_name: vehicle?.vehicle_code ?? null,
    vehicle_code: vehicle?.vehicle_code ?? null,
    vehicle,
    status: statusCode,
    status_code: statusCode,
    status_name: rawStatusName,
    // 色関連: API準拠キー名
    bg_color: bgColor || null,
    text_color: textColor || null,
    // 日時: API準拠キー名
    start_date: start,
    end_date: end,
    start: start,
    end: end,
    // その他
    booking_date: entry.booking_date ?? null,
    contact_code: normalizeString(entry.contact_code),
    customer_name: normalizeString(entry.customer_name),
    customer_email: normalizeString(entry.customer_email),
    customer_tel: normalizeString(entry.customer_tel),
    customer_address: entry.customer_address ?? null,
    past_reservation_count: entry.past_reservation_count ?? null,
    car_type: normalizeString(entry.car_type),
    re_car_type: normalizeString(entry.re_car_type),
    req_car_type: normalizeString(entry.req_car_type),
    // メール送信フラグ
    mail_send_flg1: entry.mail_send_flg1 ?? false,
    mail_send_flg2: entry.mail_send_flg2 ?? false,
    mail_send_flg3: entry.mail_send_flg3 ?? false,
    // スタッフ情報
    pu_staff: entry.pu_staff ?? null,
    do_staff: entry.do_staff ?? null,
    rent_staff: entry.rent_staff ?? null,
    return_staff: entry.return_staff ?? null,
    // 場所情報
    rental_location: entry.rental_location ?? null,
    return_location: entry.return_location ?? null,
    // 追加フィールド
    short_memo: normalizeString(entry.short_memo),
    request_coming_time: normalizeString(entry.request_coming_time),
    request_returning_time: normalizeString(entry.request_returning_time),
    // detail展開
    ...detail,
  };
};
